import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import {
  Button,
  Col,
  Empty,
  Input,
  Popconfirm,
  Row,
  Skeleton,
  Table,
  Tabs,
  Tooltip,
} from 'antd';

import {
  CheckOutlined,
  CloseOutlined,
  IssuesCloseOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import moment from 'moment';
import {
  useUpdateUserAccess,
  useUsersList,
} from '../../hooks/userManagement';

import {
  Messages,
  Notify,
  openNotification,
  util,
} from '@seims/shared';

/**
 * UserListTable – server-side pagination, search and role filter.
 */
export const UserListTable = ({
  dataState,
  filterState,
  serviceState,
  rolesState,
  syncState,
  onOpenModal,
  viewOnly,
}) => {
  const { setUsers, filteredData, setFilteredData } = dataState;
  const { searchText, setSearchText } = filterState;
  const {
    serviceType,
    setServiceType,
    coreService,
    publicService,
    backOffice,
  } = serviceState;
  const { allRoles, selectedRole } = rolesState;
  const { hasUpdated, setHasUpdated } = syncState;
  const initPageZie = 10;

  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(initPageZie);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [firstRecord, setFirstRecord] = useState(0);
  const [isNotSearchable, setIsNotSearchable] = useState(true);
  const [isSearchedResults, setIsSearchedResults] = useState(false);

  const actionLabel = viewOnly ? 'View' : 'Edit';

  const tabList = [
    {
      key: coreService,
      label: backOffice,
    },
    {
      key: publicService,
      label: "Public Web",
    }
  ];

  const computedRoleName =
    (serviceType === coreService || !selectedRole || selectedRole?.key === allRoles?.key)
      ? null
      : selectedRole.key;

  const { data, isFetching, refetch } = useUsersList({
    type: serviceType,
    first: firstRecord,
    max: pageSize,
    text: searchText || null,
    roleName: computedRoleName || null,
  });

  const { mutate: mutateAccess } = useUpdateUserAccess(serviceType);

  useEffect(() => {
    setLoading(isFetching);

    if (data) {
      setTotal(data.total);
      setUsers(data.results);
      setFilteredData(data.results);
    }

    if (hasUpdated) {
      refetch();
      setHasUpdated(false);
    }
  }, [isFetching, data, hasUpdated]);

  const showModal = (record) => onOpenModal(record, viewOnly);

  const onServiceTypeChange = (type) => {
    setFirstRecord(0);
    setCurrentPage(1);
    setSearchText('');
    setServiceType(type);
  };

  const updateUserAccess = (record, activate) => {
    const { id } = record;

    const values = {
      userId: id,
      enabled: activate,
    };

    mutateAccess(values, {
      onSuccess: () => {
        const action = activate ? 'activation' : 'deactivation';
        const msg = util.parseMessage(
          Messages.UPDATE_SUCCESS,
          `User account ${action}`
        );

        openNotification('User Access', msg, Notify.SUCCESS);

        setHasUpdated(true);
      },
      onError: () => {
        openNotification(
          'User Access',
          'SYSTEM FAILED TO UPDATE',
          Notify.ERROR
        );
      },
    });
  };

  const cancel = () => {
    const msg = util.parseMessage(Messages.UPDATE_CANCELLED);

    openNotification(
      'Action cancelled successfully',
      msg,
      Notify.ERROR
    );
  };

  const handleInputChange = (e) => {
    const sv = e.target.value;

    setSearchText(sv);
    setIsNotSearchable(sv.length === 0);

    if (sv === '' && isSearchedResults) {
      setFirstRecord(0);
      setCurrentPage(1);
    }
  };

  const handleSearchRequest = () => {
    if (searchText.length > 0) {
      setFirstRecord(0);
      setIsSearchedResults(true);
      refetch();
    }
  };

  const onPageChange = (page, size) => {
    const keep = pageSize === size;

    const first = keep ? (page - 1) * pageSize : 0;
    const newPage = keep ? page : 1;

    setFirstRecord(first);
    setPageSize(size);
    setCurrentPage(newPage);
  };

  const columns = useMemo(
    () => [
      {
        title: "Username",
        key: "username",
        render: (_, record) => (
          <>
            {!record.emailVerified ? (
              <Tooltip placement="top" title="Not Verified">
                <IssuesCloseOutlined style={{ color: "red" }} />
                <span> {record.emailAddress}</span>
              </Tooltip>
            ) : (
              record.emailAddress
            )}
          </>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Registration Date",
        dataIndex: "registrationTimestamp",
        key: "registrationTimestamp",
        render: (t) =>
          t != null
            ? moment(t).format("DD MMM YYYY, HH:mm")
            : "",
      },
      {
        title: "Last Login Date",
        dataIndex: "loginTimestamp",
        key: "loginTimestamp",
        render: (t) =>
          t != null
            ? moment(t).format("DD MMM YYYY, HH:mm")
            : "",
      },
      {
        title: "Active",
        dataIndex: "enabled",
        key: "enabled",
        render: (v) =>
          v ? (
            <CheckOutlined
              style={{
                fontSize: "18px",
                color: "#04a596",
                fontWeight: "bold",
              }}
            />
          ) : (
            <CloseOutlined
              style={{
                fontSize: "18px",
                color: "#a21b1b",
                fontWeight: "bold",
              }}
            />
          ),
      },
      {
        title: "Actions",
        dataIndex: "edit",
        key: "actions",
        render: (_, record) => (
          <>
            <a onClick={() => showModal(record)}>{actionLabel}</a>

            {record.enabled ? (
              <Popconfirm
                title="Are you sure you want to deactivate this record?"
                onConfirm={() => updateUserAccess(record, false)}
                onCancel={cancel}
                okText="Yes"
                cancelText="No"
              >
                <a style={{ marginLeft: 10 }}>Deactivate</a>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Are you sure you want to activate this record?"
                onConfirm={() => updateUserAccess(record, true)}
                onCancel={cancel}
                okText="Yes"
                cancelText="No"
              >
                <a style={{ marginLeft: 10 }}>Activate</a>
              </Popconfirm>
            )}
          </>
        ),
      },
    ],
    [actionLabel, serviceType]
  );

  return (
    <>
      <div>List of Users</div>
      <Row>
        <Col xs={16} sm={16} md={8}>
          <Input
            placeholder="Search User"
            type="text"
            className="search-bar"
            onChange={handleInputChange}
            onPressEnter={handleSearchRequest}
            onPaste={handleInputChange}
            value={searchText}
            allowClear
            size="large"
            prefix={<SearchOutlined />}
          />
        </Col>
        <Col xs={8} sm={8} >
          <Button
            disabled={isNotSearchable}
            key="Search"
            value="Search"
            type="primary"
            onClick={handleSearchRequest}
            style={{marginLeft: 10 }}
          >
            Search
          </Button>
        </Col>
      </Row>
      <div className="space"></div>
      <Tabs
          className=""
          type="card"
          onChange={onServiceTypeChange}
          items={tabList}
          key="type-tab-key-1" />
      <div className="space"></div>
      <Table
        dataSource={loading ? [] : filteredData}
        locale={{
          emptyText: loading ? <Skeleton active={true} /> : <Empty />,
        }}
        rowKey={(record) => record.id}
        columns={columns}
        bordered
        size="middle"
        mobileBreakPoint={768}
        pagination={{
          defaultCurrent: 1,
          current: currentPage,
          defaultPageSize: pageSize,
          total: total,
          pageSize: pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          onChange: onPageChange,
          pageSizeOptions: [2, 5, 10, 20, 50, 100],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ${total>1 ? 'Users' : 'User'}`,
        }}
      />
    </>
  );
};

UserListTable.propTypes = {
  dataState: PropTypes.shape({
    users: PropTypes.array.isRequired,
    setUsers: PropTypes.func.isRequired,
    filteredData: PropTypes.array.isRequired,
    setFilteredData: PropTypes.func.isRequired,
  }).isRequired,

  filterState: PropTypes.shape({
    searchText: PropTypes.string.isRequired,
    setSearchText: PropTypes.func.isRequired,
  }).isRequired,

  serviceState: PropTypes.shape({
    serviceType: PropTypes.string.isRequired,
    setServiceType: PropTypes.func.isRequired,
    coreService: PropTypes.string.isRequired,
    publicService: PropTypes.string.isRequired,
    backOffice: PropTypes.string.isRequired,
  }).isRequired,

  rolesState: PropTypes.shape({
    allRoles: PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    selectedRole: PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,

  syncState: PropTypes.shape({
    hasUpdated: PropTypes.bool.isRequired,
    setHasUpdated: PropTypes.func.isRequired,
  }).isRequired,

  onOpenModal: PropTypes.func.isRequired,
  viewOnly: PropTypes.bool.isRequired,
};